import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getExperimentVariant } from '@/lib/experiments';
import { logFirebaseEvent } from '@/lib/tracking';

interface CtaSectionProps {
  appSignUpUrl: string;
}

export default function CtaSection({ appSignUpUrl }: CtaSectionProps) {
  const [ctaVariant, setCtaVariant] = useState<'A' | 'B'>('A');

  useEffect(() => {
    const variant = getExperimentVariant('home_bottom_primary_cta', ['A', 'B']);
    setCtaVariant(variant === 'B' ? 'B' : 'A');
  }, []);

  const primaryLabel = ctaVariant === 'B' ? 'See Live Demo Flow' : 'Start Free';

  return (
    <section className="relative overflow-hidden bg-slate-950 px-6 py-20 md:py-24 text-white">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-1/2 h-96 w-[52rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/15 blur-3xl" />
      </div>
      <div className="container mx-auto max-w-4xl text-center">
        <h2 className="text-3xl font-bold text-white md:text-5xl">Build a financing experience buyers actually complete</h2>
        <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
          Keep your sales motion simple: send one link, offer monthly options, and follow up at the right moment with confidence.
        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg" className="h-12 rounded-xl bg-emerald-600 px-6 text-white hover:bg-emerald-500 hover:shadow-lg">
            <a
              href={appSignUpUrl}
              data-exp-slot="home_bottom_primary_cta"
              data-exp-variant={ctaVariant}
              onClick={() => {
                logFirebaseEvent('marketing_cta_click', {
                  slot: 'home_bottom_primary_cta',
                  variant: ctaVariant,
                  label: primaryLabel,
                });
              }}
            >
              {primaryLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <Button asChild size="lg" variant="outline" className="h-12 rounded-xl border-slate-600 bg-transparent px-6 text-slate-200 hover:bg-slate-800">
            <a href={`${appSignUpUrl.replace('/auth?mode=signup', '/auth')}`}>Book Demo</a>
          </Button>
        </div>
      </div>
    </section>
  );
}
